'use client';

import { forwardRef, useCallback, useId, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { PhotoCarouselLiveAnnouncer } from './photo-carousel-live-announcer';
import { photoCarouselRootStyles } from './styles/photo-carousel-root.styles';
import type { PhotoCarouselRootPropsInterface } from './types/photo-carousel-root.types';

/**
 * @since 10.0.0
 */
export const PhotoCarouselRoot = forwardRef<HTMLDivElement, PhotoCarouselRootPropsInterface>(
  (props: PhotoCarouselRootPropsInterface, forwardedRef) => {
    const {
      activeIndex,
      'aria-label': ariaLabel,
      children,
      className,
      css: cssProp,
      defaultActiveIndex = 0,
      onActiveIndexChange,
      shouldAwaitInteractionResponse = true,
      shouldLoop = true,
      ...rest
    } = props;
    const id = useId();

    const [activeIndexState, setActiveIndexState] = useControllableValue<number>({
      controlledValue: activeIndex ?? 0,
      uncontrolledValue: defaultActiveIndex,
      isControlled: 'activeIndex' in props,
      isUncontrolled: 'defaultActiveIndex' in props,
      onChange: onActiveIndexChange,
      componentName: 'PhotoCarousel.Root',
      controlledPropName: 'activeIndex',
      uncontrolledPropName: 'defaultActiveIndex',
    });

    // totalSlides is calculated by `PhotoCarousel.Slides`
    const [totalSlides, setTotalSlides] = useState<number>(0);

    /*
     * Looping can make the target index ambiguous, especially with 2 slides.
     * `forIndex` ties the direction hint to the index it was set for so a
     * controlled parent changing `activeIndex` directly cannot reuse a stale ±1.
     */
    const [navigation, setNavigation] = useState<{
      direction: -1 | 0 | 1;
      forIndex: number;
    }>({ direction: 0, forIndex: activeIndexState });

    const styles = css(photoCarouselRootStyles, cssProp);

    // Takes a new slide index from an event handler and
    // decides whether it needs to be adjusted based on `shouldLoop`.
    // Also runs consumer's `onActiveIndexChange` callback.
    const updateActiveIndex = useCallback(
      (index: number, direction?: -1 | 0 | 1) => {
        let newIndex = index;

        if (index > totalSlides - 1) {
          newIndex = shouldLoop ? 0 : totalSlides - 1;
        } else if (index < 0) {
          newIndex = shouldLoop ? totalSlides - 1 : 0;
        }

        if (activeIndexState !== newIndex) {
          setNavigation({
            direction: direction ?? (index > activeIndexState ? 1 : -1),
            forIndex: newIndex,
          });
          setActiveIndexState(newIndex);
        }
      },
      [activeIndexState, shouldLoop, totalSlides, setActiveIndexState],
    );

    // External controlled updates skip `updateActiveIndex`; clear the hint on
    // this render so `useSlidesRange` falls back to shortest-wrap.
    let resolvedNavigationDirection = navigation.direction;
    if (navigation.forIndex !== activeIndexState) {
      resolvedNavigationDirection = 0;
      setNavigation({ direction: 0, forIndex: activeIndexState });
    }

    const contextValue = useMemo(
      () => ({
        activeIndex: activeIndexState,
        id,
        navigationDirection: resolvedNavigationDirection,
        shouldLoop,
        setTotalSlides,
        shouldAwaitInteractionResponse,
        totalSlides,
        updateActiveIndex,
      }),
      [
        activeIndexState,
        id,
        resolvedNavigationDirection,
        shouldLoop,
        setTotalSlides,
        shouldAwaitInteractionResponse,
        totalSlides,
        updateActiveIndex,
      ],
    );

    return (
      <PhotoCarouselRootContext.Provider value={contextValue}>
        <section
          aria-label={ariaLabel}
          className={cx('c11n', 'c11n_photo-carousel__root', styles, className)}
          data-c11n-component="PhotoCarousel.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          {children}
          <PhotoCarouselLiveAnnouncer />
        </section>
      </PhotoCarouselRootContext.Provider>
    );
  },
);

PhotoCarouselRoot.displayName = 'PhotoCarousel.Root';
